import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Card, CardBody } from "../../../../shared/components/Card";
import { LinkAnchor } from "../../../../shared/components/form/FormElements";
import { AddFormButton } from "../../../../shared/components/TableElements";
import { BoldSpan, NormalSpan } from "../..";
import aiPoweredIcon from "@/shared/img/ai-powered-icon.png";
import TopUpCreditsModal from "./TopUpCreditsModal";
import AutoTopUpSettingsModal from "./AutoTopUpSettingsModal";
import { InsightsSettingsModal } from "../../../DashboardV2/insights/InsightsPanel";
import { post, postApi, putApi } from "../../../../api/getUser";
import { useToast } from "../../../../contexts/ToastContext";
import { NATEXT } from "../../../../shared/constants";

const AICreditsCard = ({
  paymentData,
  onAddPaymentMethod,
  creditsIncluded,
  creditsNextGrant,
  topUpPriceDetails,
}) => {
  const { t } = useTranslation("common");
  const { showToast } = useToast();
  const { userId, userName } = useSelector((state) => state.user);
  const users = useSelector((state) => state.users.list);

  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showAutoTopUpModal, setShowAutoTopUpModal] = useState(false);
  const [autoTopUpData, setAutoTopUpData] = useState({
    enabled: false,
    quantity: 0,
  });
  const [saveAutoTopUpLoading, setSaveAutoTopUpLoading] = useState(false);
  //const [saveTopUpLoading, setSaveTopUpLoading] = useState(false);

  const [moduleCredits, setModuleCredits] = useState({
    ai_insights: 0,
    fillup_receipt_scans: 0,
    service_invoice_scans: 0,
    voice_logs: 0,
    ai_reports: 0,
  });

  const [creditUsageOpen, setCreditUsageOpen] = useState(false);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [insightsEnabled, setInsightsEnabled] = useState(false);
  const [insightsFrequency, setInsightsFrequency] = useState(1);
  const [insightsPausedBy, setInsightsPausedBy] = useState("");
  const [insightsPausedAt, setInsightsPausedAt] = useState(null);
  const [insightsSettingsLoaded, setInsightsSettingsLoaded] = useState(false);

  let autoTopUpString = `${
    autoTopUpData.enabled ? t("enabled") : t("disabled")
  }`;
  if (autoTopUpData.enabled) {
    autoTopUpString += ` • ${autoTopUpData.quantity || 0}`;
  }

  const fetchAutoTopUpData = async () => {
    const res = await post("aiConfig", { querystring: "get_ai_credits_data" });
    if (res.success) {
      const {
        auto_topup_enabled,
        auto_topup_quantity,
        credits_remaining,
        ai_insights,
        fillup_receipt_scans,
        service_invoice_scans,
        voice_logs,
        ai_reports,
      } = res.data;
      setAutoTopUpData({
        enabled: auto_topup_enabled,
        quantity: auto_topup_quantity,
        creditsRemaining: credits_remaining,
      });
      setModuleCredits({
        ai_insights: ai_insights ?? 0,
        fillup_receipt_scans: fillup_receipt_scans ?? 0,
        service_invoice_scans: service_invoice_scans ?? 0,
        voice_logs: voice_logs ?? 0,
        ai_reports: ai_reports ?? 0,
      });
    }
  };

  const handleManageInsights = async () => {
    if (!insightsSettingsLoaded) {
      try {
        const res = await putApi({ querystring: "get_ai_insight_settings" }, "dashboard");
        if (res?.success && res?.data?.length > 0) {
          const { frequency, is_active, paused_by, paused_at } = res.data[0];
          const active = is_active === 1;
          setInsightsEnabled(active);
          setInsightsFrequency(frequency);
          if (!active) {
            const pausedUser = users.find((u) => u.id === paused_by);
            setInsightsPausedBy(pausedUser?.name ?? paused_by ?? "");
            setInsightsPausedAt(paused_at ? new Date(paused_at * 1000) : null);
          }
        }
      } catch (err) {
        console.error("Error fetching insights settings:", err);
      } finally {
        setInsightsSettingsLoaded(true);
      }
    }
    setShowInsightsModal(true);
  };

  const saveInsightsSettings = async (newEnabled, newFrequency, setLoading) => {
    const nowSec = Math.floor(Date.now() / 1000);
    const apiData = {
      is_active: newEnabled ? 1 : 0,
      frequency: Number(newFrequency),
      querystring: "save_ai_insight_settings",
      paused_by: newEnabled ? "" : userId,
      paused_at: newEnabled ? null : nowSec,
    };
    try {
      setLoading(true);
      const res = await putApi(apiData, "dashboard");
      if (res.success) {
        setInsightsEnabled(newEnabled);
        setInsightsFrequency(newFrequency);
        if (!newEnabled) {
          setInsightsPausedBy(userName ?? "");
          setInsightsPausedAt(nowSec);
        } else {
          setInsightsPausedBy("");
          setInsightsPausedAt(null);
        }
        showToast("success", t("insights_settings_saved"));
      }
    } catch (err) {
      console.error("Error saving insights settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAutoTopUpData();
  }, []);

  const handleTopUpClick = () => {
    setShowTopUpModal(true);
  };

  const handleConfigureAutoTopUpClick = () => {
    setShowAutoTopUpModal(true);
  };

  // const handlePurchase = async (purchaseData) => {
  //   const { credits } = purchaseData;
  //   const res = await post("subscription", {
  //     querystring: "create_topup_checkout_session",
  //     quantity: credits,
  //   });
  //   console.log("res", res);
  //   if (res.statusCode === 200) {
  //     window.location.href = res.body.url;
  //   }
  // };

  const handleSaveAutoTopUp = async (autoTopUpData) => {
    try {
      setSaveAutoTopUpLoading(true);
      const { enabled, quantity } = autoTopUpData;
      const res = await post("aiConfig", {
        querystring: "update_auto_topup_data",
        auto_topup_enabled: enabled,
        auto_topup_quantity: quantity,
      });
      if (res.success) {
        showToast("success", t("auto_top_up_settings_success_msg"));
        fetchAutoTopUpData();
      }
      // TODO: Implement auto top-up save logic
    } catch (error) {
      console.error("Error saving auto top-up settings:", error);
      showToast("error", t("listing_err_msg"));
    } finally {
      setSaveAutoTopUpLoading(false);
      setShowAutoTopUpModal(false);
    }
    //setShowAutoTopUpModal(false);
  };

  const handleAddPaymentMethod = () => {
    if (onAddPaymentMethod) {
      onAddPaymentMethod();
    }
    //setShowAutoTopUpModal(false);
  };

  return (
    <Card shadow="true">
      <CardBody className="d-flex flex-column gap-2" style={{ padding: "1.2rem 2.4rem" }}>
        <div className="d-flex justify-content-between align-items-center">
          {/* AI CREDITS header */}
          <div className="d-flex gap-2 align-items-center">
            <img
              src={aiPoweredIcon}
              alt="ai-powered-icon"
              width={24}
              height={24}
            />
            <div>
              <h4 style={{ margin: 0 }}>{t("ai_credits")}</h4>
              <NormalSpan style={{ fontSize: "12px", color: "#6B7280" }}>
                {t("credits_never_expire")}
              </NormalSpan>
            </div>
          </div>

          {/* Credits count + CTA */}
          <div className="d-flex gap-2 align-items-center">
            <BoldSpan style={{ fontSize: "24px", lineHeight: 1 }}>
              {t("count_credits", { count: autoTopUpData.creditsRemaining })}
            </BoldSpan>
            <AddFormButton
              type="button"
              onClick={handleTopUpClick}
              style={{
                minWidth: "min-content",
                width: "min-content",
                height: "min-content",
                padding: "0.2rem 0.9rem",
              }}
            >
              {t("top_up")}
            </AddFormButton>
          </div>
        </div>
        <hr className="my-2" />

        <div className="d-flex justify-content-between">
          <div className="d-flex flex-column gap-1">
            <NormalSpan style={{ fontSize: "14px" }}>
              {t("included_by_plan")}
            </NormalSpan>
            <BoldSpan style={{ fontSize: "14px" }}>
              {`${creditsIncluded}`}
            </BoldSpan>
          </div>

          <div className="d-flex flex-column gap-1">
            <NormalSpan style={{ fontSize: "14px" }}>
              {t("next_grant")}
            </NormalSpan>
            <BoldSpan style={{ fontSize: "14px" }}>
              {creditsNextGrant || NATEXT}
            </BoldSpan>
          </div>

          <div className="d-flex flex-column gap-1">
            <NormalSpan style={{ fontSize: "14px" }}>
              {t("auto_top_up")}
            </NormalSpan>
            <div>
              <BoldSpan style={{ fontSize: "14px" }}>
                {autoTopUpString}
              </BoldSpan>
              <span> | </span>
              <LinkAnchor onClick={handleConfigureAutoTopUpClick}>
                {t("configure")}
              </LinkAnchor>
            </div>
          </div>
        </div>

        <hr className="my-2" />

        {/* Credit usage collapsible section */}
        <div>
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => setCreditUsageOpen((v) => !v)}
          >
            <div className="d-flex gap-2 align-items-center">
              <BoldSpan style={{ fontSize: "13px" }}>{t("credit_usage")}</BoldSpan>
              <NormalSpan style={{ fontSize: "12px", color: "#6B7280" }}>{t("this_billing_cycle")}</NormalSpan>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <NormalSpan style={{ fontSize: "13px" }}>
                {t("count_credits_used", {
                  count:
                    moduleCredits.ai_insights +
                    moduleCredits.fillup_receipt_scans +
                    moduleCredits.service_invoice_scans +
                    moduleCredits.voice_logs +
                    moduleCredits.ai_reports,
                })}
              </NormalSpan>
              <i
                className={`fi fi-rr-angle-small-${creditUsageOpen ? "up" : "down"}`}
                style={{ fontSize: 12, lineHeight: 1, position: "relative", top: 1, color: "#6B7280" }}
              />
            </div>
          </div>

          {creditUsageOpen && (
            <div className="d-flex flex-column gap-1 mt-2">
              {[
                {
                  label: t("ai_insights"),
                  value: moduleCredits.ai_insights,
                  action: (
                    <LinkAnchor onClick={handleManageInsights} style={{ fontSize: "12px" }}>
                      {t("manage")} →
                    </LinkAnchor>
                  ),
                },
                { label: t("fillup_receipt_scans"), value: moduleCredits.fillup_receipt_scans },
                { label: t("service_invoice_scans"), value: moduleCredits.service_invoice_scans },
                { label: t("voice_logs_label"), value: moduleCredits.voice_logs },
                { label: t("ai_reports"), value: moduleCredits.ai_reports },
              ].map(({ label, value, action }) => (
                <div key={label} className="d-flex justify-content-between align-items-center" style={{ paddingTop: "4px", borderTop: "1px solid #F3F4F6" }}>
                  <div className="d-flex gap-2 align-items-center">
                    <NormalSpan style={{ fontSize: "13px" }}>{label}</NormalSpan>
                    {action}
                  </div>
                  <BoldSpan style={{ fontSize: "13px" }}>{value}</BoldSpan>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* <div className="d-flex gap-2 align-items-center">
          <div className="d-flex gap-1 align-items-center">
            <img
              src={aiPoweredIcon}
              alt="ai-powered-icon"
              width={16}
              height={16}
            />
            <h5 style={{ margin: 0 }}>{t("ai_credits")}</h5>
          </div>
        </div> */}

        {/* <div className="d-flex align-items-center gap-3">
          <div className="d-flex flex-column">
            <BoldSpan style={{ fontSize: "24px", lineHeight: 1 }}>
              {t("count_credits", { count: autoTopUpData.creditsRemaining })}
            </BoldSpan>
          </div>
          <AddFormButton
            type="button"
            onClick={handleTopUpClick}
            style={{
              minWidth: "min-content",
              width: "min-content",
              height: "min-content",
              padding: "0.2rem 0.9rem",
            }}
          >
            {t("top_up")}
          </AddFormButton>
        </div> */}

        {/* <NormalSpan style={{ fontSize: "12px", color: "#6B7280" }}>
          {t("credits_never_expire")}
        </NormalSpan>

        <div className="d-flex flex-column gap-1">
          <div className="d-flex justify-content-between">
            <NormalSpan style={{ fontSize: "14px" }}>
              {t("included_by_plan")}
            </NormalSpan>
            <BoldSpan style={{ fontSize: "14px" }}>
              {`${creditsIncluded}`}
            </BoldSpan>
          </div>
          <div className="d-flex justify-content-between">
            <NormalSpan style={{ fontSize: "14px" }}>
              {t("next_grant")}
            </NormalSpan>
            <BoldSpan style={{ fontSize: "14px" }}>
              {creditsNextGrant || NATEXT}
            </BoldSpan>
          </div>
          <div className="d-flex justify-content-between">
            <NormalSpan style={{ fontSize: "14px" }}>
              {t("auto_top_up")}
            </NormalSpan>
            <BoldSpan style={{ fontSize: "14px" }}>{autoTopUpString}</BoldSpan>
          </div>
        </div>

        <div className="d-flex flex-column gap-1">
          <LinkAnchor
            //style={{ fontSize: "14px" }}
            onClick={handleConfigureAutoTopUpClick}
          >
            {t("configure_auto_top_up")}
          </LinkAnchor>
        </div> */}
      </CardBody>

      {showTopUpModal && (
        <TopUpCreditsModal
          show={showTopUpModal}
          onHide={() => setShowTopUpModal(false)}
          fromScreen={"billing"}
          pricePerCredit={topUpPriceDetails?.pricePerCredit}
          currency={topUpPriceDetails?.currency}
          //onPurchase={handlePurchase}
          //saveTopUpLoading={saveTopUpLoading}
        />
      )}

      <AutoTopUpSettingsModal
        show={showAutoTopUpModal}
        onHide={() => setShowAutoTopUpModal(false)}
        onSave={handleSaveAutoTopUp}
        hasPaymentMethod={paymentData}
        onAddPaymentMethod={handleAddPaymentMethod}
        autoTopUpData={autoTopUpData}
        saveAutoTopUpLoading={saveAutoTopUpLoading}
      />

      {showInsightsModal && (
        <InsightsSettingsModal
          onClose={() => setShowInsightsModal(false)}
          isEnabled={insightsEnabled}
          frequency={insightsFrequency}
          onSave={saveInsightsSettings}
          pausedBy={insightsPausedBy}
          pausedAt={insightsPausedAt}
          hideTopUpLink
        />
      )}
    </Card>
  );
};

export default AICreditsCard;
